import { Record as record, List } from 'immutable';
import moment from 'moment';

import config from '../../../../src/config';
import Route from './Route';
import SeasonTicket from './SeasonTicket';
import Customer from './Customer';
import Address from '../../../../src/records/customer/Address';
import Payment from '../Payment';
import { CompensationClaimType } from '../../../../src/constants/AppConstants';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import { construct, resolve, resolveAll } from '../../../../src/helpers/LinkMapper';

const fieldSchema = {
    "customer": [ resolve.as(Customer), new Customer() ],
    "address": [ resolve.as(Address), new Address({ contactName: 'n/a' }) ],
    "storedAddress": [ resolve.as(Address) ],
    "routes": [ resolveAll.as(Route), List.of(new Route()) ],
    "payment": [ resolve.as(Payment), new Payment() ]
};

export default class CompensationSeasonClaim extends record(getDefaults(fieldSchema)) {
    static fromJSON(json = {}) {
        return construct(CompensationSeasonClaim, json, getResolvers(fieldSchema));
    }

    get type() {
        return CompensationClaimType.SEASON;
    }

    toJS () {
        // window.File is not serialisable, so convert to a plain object
        const { file } = this;
        let claim = this;
        if (file && this.imageId) {
            claim = this.set('file', {
                name: file.name,
                type: file.type,
                size: file.size
            });
        }
        return super.toJS.call(claim);
    }

    setUser (user) {
        let updated = this;
        if (this.customer.isUntouched()) {
            updated = this.set(
                'customer',
                this.customer.initialiseWithUser(user)
            );
        }
        return updated;
    }

    canAddRoute () {
        return (
            this.routes &&
            this.routes.size < config.get('compensationMaxRoutes')
        );
    }

    matchesItem (routeOrTicket) {
        return (r) => r.internalId === routeOrTicket.internalId;
    }

    addRoute () {
        const { routes } = this;
        return this.set('routes', routes.push(new Route()));
    }

    removeRoute (route) {
        const routes = this.routes.filterNot(this.matchesItem(route));
        return this.set('routes', routes);
    }

    updateRoute (route, updater) {
        const index = this.routes.findIndex(this.matchesItem(route));
        if (index >= 0) {
            const updated = updater(this.routes.get(index));
            return this.setIn(['routes', index], updated);
        }
        return this;
    }

    addTicket (route) {
        const { photocardNumber } = this.customer;
        const ticket = new SeasonTicket({photocardNumber});
        return this.updateRoute(
            route,
            (r) => r.set('tickets', r.tickets.push(ticket))
        );
    }

    removeTicket (route, ticket) {
        const matchesTicket = this.matchesItem(ticket);
        return this.updateRoute(
            route,
            (r) => r.set('tickets', r.tickets.filterNot(matchesTicket))
        );
    }

    updateTicket (route, ticket, updater) {
        const matchesTicket = this.matchesItem(ticket);
        return this.updateRoute(
            route,
            (r) => {
                const index = r.tickets.findIndex(matchesTicket);
                return r.setIn(['tickets', index], updater(r.tickets.get(index)));
            }
        );
    }

    updateEvidence ({route}, ticket, values) {
        return this.updateTicket(route, ticket, tkt => tkt.merge(values));
    }

    hasValidRoutes () {
        return this.routes.size > 0 && this.routes.every(r => r.isComplete());
    }

    get expectedDuration () {
        return moment.duration(config.get('compensationMinDuration'));
    }

    get expectedRouteDuration () {
        return moment.duration(config.get('compensationMinDurationPerRoute'));
    }

    get ticketsDuration () {
        return this.routes.reduce(
            (duration, route) => duration.add(route.eligibleDuration),
            moment.duration()
        );
    }

    get hasSufficientEvidence () {
        const isSufficientDuration = (actual, required) =>
            actual.as('days') >= required.as('days');

        return isSufficientDuration(this.ticketsDuration, this.expectedDuration)
            && this.routes.every(route =>
                isSufficientDuration(route.eligibleDuration, this.expectedRouteDuration)
            );
    }

    get uploadCount () {
        return this._tickets.filter((ticket) => ticket.isUploading()).size;
    }

    get failedUploadCount () {
        return this._tickets.filter((ticket) => ticket.uploadError).size;
    }

    get _tickets () {
        return this.routes.flatMap(r => r.tickets);
    }

    serializeClaims () {
        return this.routes.flatMap(route => route.serializeClaims());
    }
}
